import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { SizingMetric } from "./types";
import {
  calculateBepProximityPercent,
  calculateMotorMarginPercent
} from "@/libs/ai/metricDerivations";

export type SizingParityMetric = {
  name: string;
  unit: string;
  tolerance: number;
};

export type ParityMetricComparison = {
  frontendValue: number | null;
  serviceValue: number | null;
  unit: string;
  difference: number | null;
  matched: boolean;
  bothMissing: boolean;
};

/**
 * Shared parity comparison semantics used by golden parity, the admin parity
 * GUI, and the per-row metric tables so they cannot drift apart.
 *
 * Two engines that both report a metric as unavailable (`null`) with the same
 * unit are in agreement and therefore matched, not a mismatch.
 */
export const compareParityMetricValues = (
  definition: SizingParityMetric,
  frontendMetric?: SizingMetric,
  serviceMetric?: SizingMetric
): ParityMetricComparison => {
  const frontendValue = frontendMetric?.value ?? null;
  const serviceValue = serviceMetric?.value ?? null;
  const difference =
    frontendValue === null || serviceValue === null
      ? null
      : Math.abs(frontendValue - serviceValue);
  const unitsMatch =
    Boolean(frontendMetric) &&
    Boolean(serviceMetric) &&
    frontendMetric?.unit === serviceMetric?.unit;
  const bothMissing =
    unitsMatch && frontendValue === null && serviceValue === null;
  const matched =
    bothMissing ||
    (unitsMatch && difference !== null && difference <= definition.tolerance);
  return {
    frontendValue,
    serviceValue,
    unit: frontendMetric?.unit || serviceMetric?.unit || definition.unit,
    difference,
    matched,
    bothMissing
  };
};

export const SIZING_PARITY_METRICS: SizingParityMetric[] = [
  { name: "tdh", unit: "ft", tolerance: 0.001 },
  { name: "producedTdh", unit: "ft", tolerance: 0.001 },
  { name: "requiredStages", unit: "stages", tolerance: 1 },
  { name: "bepProximity", unit: "percent", tolerance: 0.001 },
  { name: "brakeHp", unit: "hp", tolerance: 0.001 },
  { name: "motorLoad", unit: "ratio", tolerance: 0.000001 },
  { name: "motorRatedHp", unit: "hp", tolerance: 0.001 },
  { name: "motorMarginPercent", unit: "percent", tolerance: 0.001 },
  { name: "voltageDrop", unit: "V", tolerance: 0.001 },
  { name: "cableTemperature", unit: "degF", tolerance: 0.001 },
  { name: "gasEnteringPump", unit: "ratio", tolerance: 0.000001 },
  { name: "operatingThrustLoad", unit: "lb", tolerance: 0.001 },
  { name: "pumpIntakePressure", unit: "psia", tolerance: 0.001 },
  { name: "dischargePressure", unit: "psia", tolerance: 0.001 },
  { name: "pumpIntakeTemperature", unit: "degF", tolerance: 0.001 },
  { name: "pumpAverageTemperature", unit: "degF", tolerance: 0.001 },
  { name: "dischargeTemperature", unit: "degF", tolerance: 0.001 },
  { name: "bottomholeTemperature", unit: "degF", tolerance: 0.001 }
] as const;

const refValue = <T>(value: { value: T } | T): T =>
  value && typeof value === "object" && "value" in value
    ? (value as { value: T }).value
    : (value as T);

const metric = (value: unknown, unit: string): SizingMetric => ({
  value: typeof value === "number" && Number.isFinite(value) ? value : null,
  unit
});

export const collectReportParityMetrics = (
  calcs: ScenarioCalculations
): Record<string, SizingMetric> => {
  const pumpAggregate = calcs.pumpCalculations.aggregate;
  const pumpIndividual = refValue(calcs.pumpCalculations.individual);
  const primaryPumpId = refValue(calcs.primaryPump)?.id;
  const primaryPump = primaryPumpId ? pumpIndividual[primaryPumpId] : undefined;
  const operatingLiquidRate = refValue(pumpAggregate.intakeFlowRateSTBD);
  const bepProximity = primaryPump
    ? calculateBepProximityPercent(
        operatingLiquidRate,
        primaryPump.rorMin,
        primaryPump.rorMax,
        primaryPump.rorBep
      )
    : null;
  const requiredStagesValues = Object.values(pumpIndividual)
    .map(pump => pump.requiredStages)
    .filter((value): value is number => Number.isFinite(value));
  const requiredStages = requiredStagesValues.length
    ? requiredStagesValues.reduce((sum, value) => sum + value, 0)
    : null;
  const ratedMotorHp = refValue(calcs.motorCalculations.reratedNameplateHP);
  const loadMotorHp = refValue(calcs.motorCalculations.totalRequiredBrakeHP);
  const motorMarginPercent =
    Number.isFinite(ratedMotorHp) && Number.isFinite(loadMotorHp)
      ? calculateMotorMarginPercent(ratedMotorHp, loadMotorHp)
      : null;

  return {
    tdh: metric(refValue(pumpAggregate.requiredTotalDynamicHeadFt), "ft"),
    producedTdh: metric(
      refValue(pumpAggregate.producedTotalDynamicHeadFt),
      "ft"
    ),
    requiredStages: metric(requiredStages, "stages"),
    bepProximity: metric(bepProximity, "percent"),
    brakeHp: metric(refValue(pumpAggregate.requiredPumpBrakeHP), "hp"),
    motorLoad: metric(
      refValue(calcs.motorCalculations.motorLoadRatio),
      "ratio"
    ),
    motorRatedHp: metric(ratedMotorHp, "hp"),
    motorMarginPercent: metric(motorMarginPercent, "percent"),
    voltageDrop: metric(
      refValue(calcs.cableCalculations.aggregate.totalCablesVoltLossV),
      "V"
    ),
    cableTemperature: metric(
      refValue(
        calcs.cableCalculations.aggregate.totalCablesAverageTemperatureF
      ),
      "degF"
    ),
    gasEnteringPump: metric(
      refValue(calcs.intakeCalculations.aggregate.gasEnteringPumpRatio),
      "ratio"
    ),
    operatingThrustLoad: metric(
      refValue(pumpAggregate.operatingThrustLoad),
      "lb"
    ),
    pumpIntakePressure: metric(refValue(calcs.pumpIntakePressurePSIA), "psia"),
    dischargePressure: metric(
      refValue(pumpAggregate.producedDischargePressurePSIA),
      "psia"
    ),
    pumpIntakeTemperature: metric(
      refValue(calcs.pumpIntakeTemperatureF),
      "degF"
    ),
    pumpAverageTemperature: metric(
      refValue(calcs.pumpAverageTemperatureF),
      "degF"
    ),
    dischargeTemperature: metric(
      refValue(pumpAggregate.dischargeTemperatureF),
      "degF"
    ),
    bottomholeTemperature: metric(
      refValue(calcs.bottomholeTemperatureF),
      "degF"
    )
  };
};
